import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { TrendingUp, ShieldCheck, Wrench, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const EMPTY = {
  owner_name: "", email: "", phone: "", villa_name: "", location: "",
  property_type: "", bedrooms: "", area: "", expected_rate: "", amenities: "", description: "",
};

const BENEFITS = [
  { icon: TrendingUp, title: "Higher revenue, low effort", text: "Dynamic pricing, marketing and a dedicated manager working for your villa." },
  { icon: Wrench, title: "End-to-end management", text: "Complete ownership of upkeep, housekeeping, staff and maintenance." },
  { icon: ShieldCheck, title: "Transparent profit-sharing", text: "Fair revenue share with clear monthly reporting — no hidden costs." },
];

const TYPES = ["Pine Wooden Villa", "Bungalow", "Farmhouse", "Apartment", "Land", "Other"];

export default function VillaOnboarding() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.owner_name || !form.email || !form.phone || !form.villa_name || !form.location || !form.property_type) {
      return toast.error("Please fill all required fields.");
    }
    setSubmitting(true);
    try {
      await api.post("/onboarding", { ...form, bedrooms: Number(form.bedrooms) || 0 });
      toast.success("Thanks! Our onboarding team will reach out within 24 hours.");
      setForm(EMPTY);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="onboarding-page">
      {/* Hero */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Rent-O-Villa</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light mt-3 max-w-3xl leading-tight">
            List your villa. We handle the rest.
          </h1>
          <p className="text-primary-foreground/75 mt-5 max-w-xl text-lg">
            Partner with LAVista for rental management with profit-sharing and complete ownership of
            upkeep and maintenance of your property.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-7">
        {BENEFITS.map((b, i) => {
          const Icon = b.icon;
          return (
            <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-card border border-border rounded-lg p-7">
              <Icon className="h-8 w-8 text-accent mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-normal">{b.title}</h3>
              <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{b.text}</p>
            </motion.div>
          );
        })}
      </section>

      {/* Form */}
      <section className="bg-secondary">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-light text-secondary-foreground">Tell us about your property</h2>
          <p className="text-secondary-foreground/70 mt-2">Fields marked * are required.</p>

          {done ? (
            <div className="mt-8 bg-card border border-border rounded-lg p-10 text-center" data-testid="onboarding-success">
              <Check className="h-12 w-12 text-primary mx-auto mb-3" />
              <h3 className="font-heading text-2xl">Submission received</h3>
              <p className="text-muted-foreground mt-2">Our onboarding team will contact you within 24 hours.</p>
              <Button className="mt-6 rounded-full" data-testid="onboarding-again-btn" onClick={() => setDone(false)}>Submit another property</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 bg-card border border-border rounded-lg p-8 space-y-5" data-testid="onboarding-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2"><Label>Owner name *</Label><Input data-testid="ob-owner-name" value={form.owner_name} onChange={(e) => update("owner_name", e.target.value)} /></div>
                <div className="space-y-2"><Label>Phone *</Label><Input data-testid="ob-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} /></div>
              </div>
              <div className="space-y-2"><Label>Email *</Label><Input type="email" data-testid="ob-email" value={form.email} onChange={(e) => update("email", e.target.value)} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2"><Label>Villa / property name *</Label><Input data-testid="ob-villa-name" value={form.villa_name} onChange={(e) => update("villa_name", e.target.value)} /></div>
                <div className="space-y-2"><Label>Location *</Label><Input data-testid="ob-location" value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="City, State" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label>Property type *</Label>
                  <Select value={form.property_type} onValueChange={(v) => update("property_type", v)}>
                    <SelectTrigger data-testid="ob-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Bedrooms</Label><Input type="number" min="0" data-testid="ob-bedrooms" value={form.bedrooms} onChange={(e) => update("bedrooms", e.target.value)} /></div>
                <div className="space-y-2"><Label>Area</Label><Input data-testid="ob-area" value={form.area} onChange={(e) => update("area", e.target.value)} placeholder="e.g. 2400 sq.ft" /></div>
              </div>
              <div className="space-y-2"><Label>Expected nightly rate</Label><Input data-testid="ob-rate" value={form.expected_rate} onChange={(e) => update("expected_rate", e.target.value)} placeholder="e.g. Rs. 20,000" /></div>
              <div className="space-y-2"><Label>Key amenities</Label><Input data-testid="ob-amenities" value={form.amenities} onChange={(e) => update("amenities", e.target.value)} placeholder="Pool, garden, EV charger..." /></div>
              <div className="space-y-2"><Label>Anything else?</Label><Textarea data-testid="ob-description" rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Tell us more about your property" /></div>
              <Button type="submit" disabled={submitting} data-testid="onboarding-submit-btn" className="w-full rounded-full h-12 text-base">
                {submitting ? "Submitting..." : "List My Property"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
